/**
 * In-App Purchase Service
 * Handles StoreKit purchases on iOS via Capacitor plugin
 * Falls back gracefully on web (all methods return null/false)
 */

import { Capacitor } from '@capacitor/core';
import { isNativeIOS } from '../utils/platform';

// Product IDs — must match App Store Connect configuration
export const IAP_PRODUCTS = {
  PREMIUM_MONTHLY: 'com.zealgenesis.premium.monthly',
  PREMIUM_YEARLY: 'com.zealgenesis.premium.yearly',
  BLUEPRINT_RELATIONSHIP: 'com.zealgenesis.blueprint.relationship.v1',
  BLUEPRINT_PARENTING: 'com.zealgenesis.blueprint.parenting.v1',
  BLUEPRINT_TIMING: 'com.zealgenesis.blueprint.timing.v1',
  BLUEPRINT_BUNDLE: 'com.zealgenesis.blueprint.bundle.v1',
} as const;

export interface IAPProduct {
  productId: string;
  title: string;
  description: string;
  price: string;
  priceAmount: number;
  currency: string;
}

export interface IAPPurchaseResult {
  success: boolean;
  transactionId?: string;
  receipt?: string;
  error?: string;
}

// Dynamic import to avoid bundling the plugin on web
let _iapPlugin: any = null;
let _configured = false;
let _configuredUserId: string | null = null;

// Diagnostic trace of the IAP flow — shown on the paywall in TestFlight so we
// can debug device-side StoreKit/RevenueCat failures without a Mac.
let _trace: string[] = [];
function trace(msg: string) {
  _trace.push(`${new Date().toISOString().slice(11, 19)} ${msg}`);
  if (_trace.length > 30) _trace = _trace.slice(-30);
  console.log('[IAP]', msg);
}
export function getIAPTrace(): string[] { return [..._trace]; }

// Capture uncaught errors / rejections into the trace — a silently dying
// promise chain is the prime suspect for a trace that stops mid-flow.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => trace(`WINDOW ERROR: ${e.message} @${e.filename?.split('/').pop()}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e: any) => trace(`UNHANDLED REJECTION: ${e.reason?.message || e.reason}`));
}

async function getPlugin() {
  if (!isNativeIOS()) {
    trace(`getPlugin: NOT native iOS (platform=${Capacitor.getPlatform()}, native=${Capacitor.isNativePlatform()})`);
    return null;
  }
  if (_iapPlugin) return _iapPlugin;
  try {
    // Official RevenueCat plugin (SPM-compatible). The previous
    // @capgo/capacitor-purchases plugin had no Package.swift, so it was
    // silently excluded from the Capacitor 8 SPM iOS build — native side
    // never existed and ALL IAP calls no-oped.
    const mod = await import('@revenuecat/purchases-capacitor');
    // CRITICAL: never store/return the raw Capacitor plugin proxy from an
    // async function. The proxy converts EVERY property access into a native
    // call — including the runtime's automatic `.then` thenable check when an
    // async function returns it — producing:
    //   UNHANDLED REJECTION: "Purchases.then()" is not implemented on ios
    // and hanging every awaited call site (root cause of Builds 92–102 IAP
    // failures: loading=true forever, products=0, prices as "…").
    // Wrap in a plain object so only real methods ever touch the proxy.
    const P = mod.Purchases;
    _iapPlugin = {
      configure: (opts: any) => P.configure(opts),
      getOfferings: () => P.getOfferings(),
      purchasePackage: (opts: any) => P.purchasePackage(opts),
      restorePurchases: () => P.restorePurchases(),
      getCustomerInfo: () => P.getCustomerInfo(),
      logIn: (opts: { appUserID: string }) => P.logIn(opts),
    };
    trace(`plugin loaded OK (isPluginAvailable=${Capacitor.isPluginAvailable('Purchases')}, wrapped)`);
    return _iapPlugin;
  } catch (err: any) {
    trace(`plugin import FAILED: ${err?.message || err}`);
    return null;
  }
}

/**
 * Initialize the IAP plugin with RevenueCat API key
 * Call once on app startup (only does anything on iOS)
 */
export async function initializeIAP(userId: string): Promise<void> {
  trace(`initializeIAP called (user=${userId ? userId.slice(0, 8) : 'NONE'})`);
  const plugin = await getPlugin();
  if (!plugin) { trace('initializeIAP: no plugin'); return; }
  if (_configured) {
    if (_configuredUserId === userId) {
      trace('initializeIAP: already configured for this user');
      return;
    }

    // RevenueCat can only be configured once per app process. When a person
    // changes Zeal accounts, explicitly switch the SDK identity instead of
    // continuing to read and purchase against the previous account.
    trace(`logIn() switching RevenueCat user to ${userId.slice(0, 8)}…`);
    await plugin.logIn({ appUserID: userId });
    _configuredUserId = userId;
    trace('logIn() OK');
    return;
  }

  // RevenueCat iOS API key (public, safe to commit)
  // This key is bundled in the app and visible to users - it's not sensitive
  // "Zeal Genesis (App Store)" public SDK key from the RevenueCat dashboard.
  // NOTE: previous fallback appl_XhgeQacY... was from the wrong project and
  // caused empty offerings / broken purchases (fixed Jul 13, 2026).
  const apiKey = process.env.REACT_APP_REVENUECAT_IOS_KEY || 'appl_LjXpPThGNsIXmDADcaGkiNVwFOt';
  
  if (!apiKey) {
    console.error('[IAP] CRITICAL: RevenueCat API key not configured! Purchases will fail.');
    return;
  }

  try {
    trace(`configure() starting, key ${apiKey.slice(0, 10)}…, user ${userId.slice(0, 8)}…`);
    await plugin.configure({
      apiKey,
      appUserID: userId,
    });
    _configured = true;
    _configuredUserId = userId;
    trace('configure() OK');
  } catch (err: any) {
    trace(`configure() FAILED: ${err?.code || ''} ${err?.message || err}`);
    throw err;  // Re-throw so caller knows it failed
  }
}

/**
 * Ensure RevenueCat is configured before any store call.
 * Guards against purchase attempts racing ahead of app-startup initialization.
 */
export async function ensureIAPReady(userId?: string): Promise<boolean> {
  trace('ensureIAPReady: entered');
  const plugin = await getPlugin();
  if (!plugin) { trace('ensureIAPReady: no plugin'); return false; }
  const uid = userId || _configuredUserId;
  if (!uid) { trace('ensureIAPReady: no userId available'); return false; }
  if (_configured && _configuredUserId === uid) {
    trace('ensureIAPReady: already configured for this user');
    return true;
  }
  try {
    await initializeIAP(uid);
    trace(`ensureIAPReady: done, configured=${_configured}`);
    return _configured;
  } catch (err: any) {
    trace(`ensureIAPReady: initializeIAP threw: ${err?.message || err}`);
    return false;
  }
}

/**
 * Get available products from the App Store
 */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
}

export async function getProducts(userId?: string): Promise<IAPProduct[]> {
  trace(`getProducts called (user=${userId ? userId.slice(0, 8) : 'NONE'})`);
  const plugin = await getPlugin();
  trace(`getProducts: plugin resolved (${plugin ? 'ok' : 'null'})`);
  if (!plugin) { trace('getProducts: no plugin, aborting'); return []; }

  try {
    // Ensure RevenueCat is configured before fetching offerings — otherwise
    // this can race startup initialization and return an empty product list.
    const ready = await withTimeout(ensureIAPReady(userId), 15000, 'IAP init');
    if (!ready) { trace('getProducts: init not ready, aborting'); return []; }
    trace('getOfferings() starting…');
    const offRes: any = await withTimeout<any>(plugin.getOfferings(), 20000, 'getOfferings');
    const offerings = offRes?.offerings ?? offRes; // handle wrapped/unwrapped responses
    trace(`getOfferings OK. current=${offerings?.current?.identifier ?? 'NONE'} packages=${offerings?.current?.availablePackages?.length ?? 0} all=[${offerings?.all ? Object.keys(offerings.all).join(',') : ''}]`);
    if (!offerings?.current?.availablePackages) { trace('getProducts: no availablePackages, returning []'); return []; }

    const mapped = offerings.current.availablePackages.map((pkg: any) => {
      const prod = pkg.product || {};
      // Prefer the store-formatted price; fall back to formatting the raw
      // amount so the paywall always shows a price (3.1.2c requirement).
      let price = prod.priceString || '';
      if (!price && typeof prod.price === 'number' && prod.price > 0) {
        try {
          price = new Intl.NumberFormat(undefined, { style: 'currency', currency: prod.currencyCode || 'USD' }).format(prod.price);
        } catch {
          price = `$${prod.price.toFixed(2)}`;
        }
      }
      trace(`pkg ${pkg.identifier} -> ${prod.identifier} price=${prod.priceString || prod.price || '?'}`);
      return {
        productId: prod.identifier,
        title: prod.title,
        description: prod.description,
        price,
        priceAmount: prod.price,
        currency: prod.currencyCode,
      };
    });
    trace(`getProducts done: ${mapped.length} products`);
    return mapped;
  } catch (err: any) {
    trace(`getProducts FAILED: ${err?.code || ''} ${err?.message || err}`);
    return [];
  }
}

/**
 * Purchase a subscription or product
 */
export async function purchaseProduct(productId: string, userId?: string): Promise<IAPPurchaseResult> {
  const plugin = await getPlugin();
  if (!plugin) return { success: false, error: 'IAP not available on this device' };

  const ready = await ensureIAPReady(userId);
  if (!ready) {
    trace(`purchase ${productId}: init not ready`);
    return { success: false, error: 'The App Store connection is not ready. Please try again in a moment.' };
  }

  trace(`purchase requested: ${productId}`);

  try {
    const offRes: any = await withTimeout<any>(plugin.getOfferings(), 20000, 'getOfferings');
    const offerings = offRes?.offerings ?? offRes;
    trace(`purchase: offerings current=${offerings?.current?.identifier ?? 'NONE'} packages=${offerings?.current?.availablePackages?.length || 0}`);

    const pkg = offerings?.current?.availablePackages?.find(
      (p: any) => p.product.identifier === productId
    );

    if (!pkg) {
      trace(`purchase: product NOT FOUND. available=[${offerings?.current?.availablePackages?.map((p: any) => p.product.identifier).join(',') || ''}]`);
      return { success: false, error: 'This product is temporarily unavailable in the App Store. Please try again later.' };
    }

    trace(`purchasePackage ${pkg.identifier} in ${pkg.offeringIdentifier || offerings?.current?.identifier || '?'}`);
    // Official plugin API: purchasePackage({ aPackage })
    const result: any = await plugin.purchasePackage({ aPackage: pkg });

    if (result?.customerInfo) {
      return {
        success: true,
        transactionId: result.customerInfo.originalAppUserId,
      };
    }

    return { success: false, error: 'Purchase not completed' };
  } catch (err: any) {
    trace(`purchase FAILED: ${err?.code || ''} ${err?.message || err} cancelled=${err?.userCancelled}`);
    const msg = String(err?.message || '').toLowerCase();
    if (err?.userCancelled || err?.code === 'PURCHASE_CANCELLED' || err?.code === '1' || msg.includes('cancel')) {
      return { success: false, error: 'cancelled' };
    }
    return { success: false, error: err?.message || 'Purchase failed' };
  }
}

/**
 * Restore previous purchases (required by Apple)
 */
export async function restorePurchases(): Promise<{ isPremium: boolean; ownedProducts: string[] }> {
  const plugin = await getPlugin();
  if (!plugin) return { isPremium: false, ownedProducts: [] };

  try {
    // Timeout so the UI never hangs on "Restoring..."
    const restoreRes: any = await withTimeout<any>(plugin.restorePurchases(), 20000, 'restorePurchases');
    const customerInfo = restoreRes?.customerInfo ?? restoreRes;
    const entitlements = customerInfo?.entitlements?.active || {};
    const isPremium = !!entitlements['premium'];
    const ownedProducts = Object.keys(entitlements);

    return { isPremium, ownedProducts };
  } catch (err: any) {
    console.warn('[IAP] restore error:', err?.message);
    return { isPremium: false, ownedProducts: [] };
  }
}

/**
 * Check current subscription status via RevenueCat
 */
export async function checkIAPSubscription(userId?: string): Promise<{
  isPremium: boolean;
  expiresDate?: string;
  productId?: string;
}> {
  const plugin = await getPlugin();
  if (!plugin) return { isPremium: false };
  // getCustomerInfo requires configure() to have run — wait for it instead of
  // racing app-startup initialization and silently failing.
  const ready = await ensureIAPReady(userId);
  if (!ready) { trace('checkIAPSubscription: not configured, skipping'); return { isPremium: false }; }

  try {
    trace('getCustomerInfo() starting…');
    const infoRes: any = await withTimeout<any>(plugin.getCustomerInfo(), 15000, 'getCustomerInfo');
    const customerInfo = infoRes?.customerInfo ?? infoRes;
    const entitlements = customerInfo?.entitlements?.active || {};
    const premium = entitlements['premium'];

    if (premium) {
      return {
        isPremium: true,
        expiresDate: premium.expirationDate,
        productId: premium.productIdentifier,
      };
    }

    trace('getCustomerInfo OK (no premium entitlement)');
    return { isPremium: false };
  } catch (err: any) {
    trace(`getCustomerInfo FAILED: ${err?.code || ''} ${err?.message || err}`);
    return { isPremium: false };
  }
}

/**
 * Whether IAP should be used for purchases (true on native iOS)
 */
export function shouldUseIAP(): boolean {
  return isNativeIOS();
}
